import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { formatAmountUnitDecimalPlace } from "@/utility/common";
import { dateFunc } from "@/components/Functions/dateFunc";
import { useTranslation } from "react-i18next";

const useDefaultUnitDetails = () => {
  const { defaultUnit } = useSelector((state: any) => state.case);
  const decimalPlace = defaultUnit?.decimalPlace;
  const defaultUnitName = defaultUnit?.name;
  return { decimalPlace, defaultUnitName };
};

const CustomHeader = () => {
  const { defaultUnitName } = useDefaultUnitDetails();
  const unitName = defaultUnitName;
  return `(${unitName})`;
};
const amountCellRenderer = (field: string) => (params: any) => {
  const { decimalPlace, defaultUnitName } = useDefaultUnitDetails();
  const amount = params.data[field];
  const formattedDefaultAmount = formatAmountUnitDecimalPlace(
    amount,
    decimalPlace
  );
  return formattedDefaultAmount !== "-"
    ? `${formattedDefaultAmount} ${defaultUnitName}`
    : formattedDefaultAmount;
};

export const useColumnDefs = ({
  caseId,
  debtorId,
  editId,
  abbreviation,
}: {
  caseId: string | undefined;
  debtorId: string | undefined;
  editId: string | undefined;
  abbreviation: string | undefined;
}) => {
  const { t } = useTranslation();
  return [
    {
      cellRenderer: function (params: any) {
        const thisId = debtorId ? debtorId : editId;
        const prefix = thisId
          ? `/case/${caseId}/debtor/${thisId}`
          : `/case/${caseId}`;
        return (
          <>
            <Link
              to={`${prefix}/claim/${params.data.claimId}`}
              state={{ from: params.data.claimId }}
            >
              <span className="text-bold-underline">
                {params.data.claimReferenceId}
              </span>
            </Link>
          </>
        );
      },
      comparator: () => 0,
      field: "claimReferenceId",
      type: "claimReferenceIdType",

      headerName: t("pages.debtors.debtorClaimsSection.columnsDetails.claimId"),
      unSortIcon: true,
      sortable: true,
      cellStyle: {
        whiteSpace: "normal",
        lineHeight: "1.5",
      },
    },
    {
      cellRenderer: function (params: any) {
        return (
          <>
            <Link
              to={`/case/${caseId}/creditor/${params.data.creditorId}`}
              state={{ from: params.data.creditorId }}
              style={{ fontWeight: 200 }}
            >
              <span className="text-bold-underline">
                {params.data.creditorName}
              </span>
            </Link>
          </>
        );
      },
      comparator: () => 0,
      field: "creditorName",
      type: "creditorNameType",
      headerName: t(
        "pages.debtors.debtorClaimsSection.columnsDetails.creditor"
      ),
      unSortIcon: true,
      sortable: true,
    },
    {
      comparator: () => 0,
      field: "assigneeName",
      type: "assigneeNameType",
      headerName: t(
        "pages.debtors.debtorClaimsSection.columnsDetails.assignee"
      ),
      unSortIcon: true,
      sortable: true,
      cellRenderer: function (param: any) {
        return param.data.assigneeName !== "" ? (
          <Link
            to={`/case/${caseId}/creditor/${param.data.assigneeId}`}
            state={{ from: param.data.assigneeId }}
            style={{ fontWeight: 200 }}
          >
            <span className="text-bold-underline">
              {param.data.assigneeName}
            </span>
          </Link>
        ) : (
          "-"
        );
      },
    },
    {
      cellRenderer: amountCellRenderer("unresolvedCreditorAmount"),
      comparator: () => 0,
      field: "unresolvedCreditorAmount",
      type: "unresolvedCreditorAmountType",
      headerClass: "stepHeaderClass",
      unSortIcon: true,
      sortable: true,
      cellClass: "align-right",
      headerName: t(
        "pages.debtors.debtorClaimsSection.columnsDetails.unResolvedCreditorAmount",
        { unitName: CustomHeader() }
      ),
    },
    {
      cellRenderer: amountCellRenderer("unresolvedDebtorAmount"),
      comparator: () => 0,
      field: "unresolvedDebtorAmount",
      type: "unresolvedDebtorAmountType",
      headerClass: "stepHeaderClass",
      unSortIcon: true,
      sortable: true,
      cellClass: "align-right",
      headerName: t(
        "pages.debtors.debtorClaimsSection.columnsDetails.unResolvedDebtorAmount",
        { unitName: CustomHeader() }
      ),
    },
    {
      cellRenderer: amountCellRenderer("resolvedAmount"),
      comparator: () => 0,
      field: "resolvedAmount",
      type: "resolvedAmountType",
      headerClass: "stepHeaderClass",
      unSortIcon: true,
      sortable: true,
      cellClass: "align-right",
      headerName: t(
        "pages.debtors.debtorClaimsSection.columnsDetails.resolvedAmount",
        { unitName: CustomHeader() }
      ),
    },
    {
      cellRenderer: amountCellRenderer("originalAmount"),
      comparator: () => 0,
      field: "originalAmount",
      type: "originalAmountType",
      headerClass: "stepHeaderClass",
      unSortIcon: true,
      sortable: true,
      cellClass: "align-right",
      headerName: t(
        "pages.debtors.debtorClaimsSection.columnsDetails.originalAmount",
        { unitName: CustomHeader() }
      ),
    },
    {
      cellRenderer: amountCellRenderer("currentAmount"),
      comparator: () => 0,
      field: "currentAmount",
      type: "currentAmountType",
      headerClass: "stepHeaderClass",
      unSortIcon: true,
      sortable: true,
      cellClass: "align-right",
      headerName: t(
        "pages.debtors.debtorClaimsSection.columnsDetails.currentAmount",
        { unitName: CustomHeader() }
      ),
    },
    {
      comparator: () => 0,
      field: "claimStageName",
      type: "claimStageNameType",
      headerName: t("pages.debtors.debtorClaimsSection.columnsDetails.stage"),

      unSortIcon: true,
      sortable: true,
    },
    {
      comparator: () => 0,
      field: "actionStatusName",
      type: "actionStatusNameType",
      headerName: t("pages.debtors.debtorClaimsSection.columnsDetails.status"),

      unSortIcon: true,
      sortable: true,
    },
    {
      comparator: () => 0,
      field: "updatedOn",
      type: "updatedOnType",
      headerName: t(
        "pages.debtors.debtorClaimsSection.columnsDetails.lastUpdated"
      ),
      unSortIcon: true,
      sortable: true,
      cellRenderer: function (param: any) {
        return (
          dateFunc(param.data.updatedOn).formattedDateTime + " " + abbreviation
        );
      },
    },
    {
      comparator: () => 0,
      flex: 1,
      field: "exclude",
      headerName: "",
      cellRenderer: "excludeRenderer",
    },
  ];
};
